/**
 * salesPlayDemo.js
 * "Generate demo output" for Sales Studio plays: loads a handful of real
 * contacts and renders one image for a picked contact from the editor's
 * unsaved settings, with the same variables and context a real send uses.
 */

import { collection, getDocs, limit, query } from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../utils/cloudFunctions";
import { buildImageContextText, buildImagePromptVariables } from "./EmailAiModes/dynamicImageUtils";

const POOL_SIZE = 150;

export const loadDemoContacts = async (userId, { needsWebsite = false } = {}) => {
  const snap = await getDocs(query(collection(firestore, `World/${userId}/db`), limit(POOL_SIZE)));
  return snap.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((c) => !c.trashedAt && c.business_name && (!needsWebsite || c.lead_website));
};

export const pickRandomContacts = (pool, count = 4) => {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
};

export const renderDemoImage = async ({ userId, draftAsset, contact, userSettings }) => {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateDynamicImage(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userId,
      draftAsset,
      contactId: contact.id,
      variables: buildImagePromptVariables(contact, { userSettings }),
      contextText: buildImageContextText(contact),
      force: true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.imageUrl) {
    return { imageUrl: data.imageUrl, storagePath: data.storagePath || "", scene: data.scene || null };
  }
  if (res.status === 402) {
    throw new Error(
      `Not enough credits for the demo (needs ${data.creditsRequired ?? "a few"}, balance ${data.creditBalance ?? 0}).`,
    );
  }
  throw new Error(data.details || data.error || "The demo could not be generated. Please try again.");
};

// "Use every time": the server keeps a copy of the demo outside the retention
// cleanup and returns its permanent URL.
export const pinDemoImage = async ({ userId, storagePath }) => {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateDynamicImage(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, pinStoragePath: storagePath }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.imageUrl) return { imageUrl: data.imageUrl, storagePath: data.storagePath };
  throw new Error(data.error || "This GIF could not be kept. Please try again.");
};
